import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../../components/MobileLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const Terms: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAuthenticated = !!user;

  return (
    <MobileLayout showNav={isAuthenticated} showHeader={isAuthenticated}>
      <div className="flex-1 flex flex-col">
        {/* Header - Only show when not authenticated */}
        {!isAuthenticated && (
          <div className="px-6 py-4 border-b border-dark-border dark:border-dark-border border-light-border">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface"
              >
                <ArrowLeftIcon className="w-5 h-5 text-dark-text dark:text-dark-text text-light-text" />
              </button>
              <h1 className="ml-4 text-xl font-semibold text-dark-text dark:text-dark-text text-light-text">
                {t('legal.terms.title', 'Terms of Service')}
              </h1>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="prose prose-invert max-w-none">
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t('legal.terms.lastUpdated', 'Last updated')}:{' '}
              {new Date().toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US')}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              1. {t('legal.terms.acceptance.title', 'Acceptance of Terms')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.terms.acceptance.content',
                'By accessing and using Chat Nexus, you agree to be bound by these Terms of Service and all applicable laws and regulations.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              2. {t('legal.terms.useOfService.title', 'Use of Service')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.terms.useOfService.content',
                'You must be at least 18 years old to use this service. You are responsible for maintaining the confidentiality of your account and password.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              3. {t('legal.terms.aiCompanions.title', 'AI Companions')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.terms.aiCompanions.content',
                'AI companions are artificial intelligence entities. They do not represent real people and should not be treated as such. All conversations are subject to our content moderation policies.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              4. {t('legal.terms.privacy.title', 'Privacy')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.terms.privacy.content',
                'Your privacy is important to us. Please review our Privacy Policy to understand how we collect and use your information.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              5. {t('legal.terms.payments.title', 'Payments and Subscriptions')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.terms.payments.content',
                'Subscriptions are billed monthly. Credits do not expire. All payments are processed securely through Stripe. Refunds are subject to our refund policy.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              6. {t('legal.terms.contentGuidelines.title', 'Content Guidelines')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.terms.contentGuidelines.content',
                'You agree not to use the service for any illegal, harmful, or inappropriate purposes. We reserve the right to moderate and remove content that violates our guidelines.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              7. {t('legal.terms.termination.title', 'Termination')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-4">
              {t(
                'legal.terms.termination.content',
                'We reserve the right to terminate or suspend your account at any time for violations of these terms.',
              )}
            </p>

            <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-3">
              8. {t('legal.terms.contact.title', 'Contact')}
            </h2>
            <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
              {t(
                'legal.terms.contact.content',
                'For questions about these Terms, please contact us at support@chatnexus.com',
              )}
            </p>
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default Terms;
